/**
 * whySize — Shared "why this size?" rationale logic + popover component.
 *
 * Extracted from AdjustMixPanel (iter45g) so the Recommended Mix card on
 * Results.jsx can surface the same green/grey diff popover — lenders
 * shouldn't have to scroll to the Adjust panel just to see the same
 * rationale that will print on the paid PDF.
 *
 * `buildWhySizeParts` and `buildWhySizeRationale` mirror the Python
 * `report_v3/mapper.py::build_report_data` character-for-character. Keep
 * them in lockstep — same phrasing, same plurals, same clause order.
 */
import { useEffect, useRef, useState } from "react";
import { Info, Sparkles, X } from "lucide-react";

// Format a MW value the way the mapper does — drop trailing ".0" so
// "5 MW" reads naturally next to "1.5 MW". Mirrors _fmt_mw() in the
// Python mapper.
const _fmtMw = (v) => {
  const n = Number(v || 0);
  return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(4)));
};

export const buildWhySizeParts = (currentPick, recPick, language = "en",
                                    hasRecommendedBaseline = true) => {
  const fr = language === "fr";
  const count = Number(currentPick?.count || 0);
  if (count <= 0) return { buyerNotes: [], autoReasoning: "" };
  const unitMw = Number(currentPick?.unit_mw || 0);
  const recCount = Number(recPick?.count || 0);
  const recUnitMw = Number(recPick?.unit_mw || 0);
  const autoReasoning = String(currentPick?.reasoning || "").trim()
    || String(recPick?.reasoning || "").trim();
  const notes = [];
  if (hasRecommendedBaseline) {
    const countChanged = recCount > 0 && count !== recCount;
    const sizeChanged = recUnitMw > 0 && Math.abs(unitMw - recUnitMw) > 1e-6;
    const newlyAdded = recCount === 0;
    if (newlyAdded) {
      notes.push(fr
        ? `Ajouté par l'acheteur (${count} × ${_fmtMw(unitMw)} MW) — cette technologie ne figurait pas dans le mix recommandé.`
        : `Added by buyer (${count} × ${_fmtMw(unitMw)} MW) — this technology was not in the recommended mix.`);
    } else {
      if (countChanged) {
        const delta = count - recCount;
        const abs = Math.abs(delta);
        if (fr) {
          const noun = abs > 1 ? "unités" : "unité";
          notes.push(delta > 0
            ? `+${delta} ${noun} ajoutée${abs > 1 ? "s" : ""} par l'acheteur pour renforcer la capacité ferme (${recCount} → ${count}).`
            : `${delta} ${noun} retirée${abs > 1 ? "s" : ""} par l'acheteur (${recCount} → ${count}).`);
        } else {
          const noun = abs !== 1 ? "units" : "unit";
          notes.push(delta > 0
            ? `+${delta} ${noun} added by buyer to lift firm capacity (${recCount} → ${count}).`
            : `${delta} ${noun} removed by buyer (${recCount} → ${count}).`);
        }
      }
      if (sizeChanged) {
        notes.push(fr
          ? `Taille par unité ajustée par l'acheteur : ${_fmtMw(recUnitMw)} MW → ${_fmtMw(unitMw)} MW.`
          : `Unit size adjusted by buyer: ${_fmtMw(recUnitMw)} MW → ${_fmtMw(unitMw)} MW.`);
      }
    }
  }
  return { buyerNotes: notes, autoReasoning };
};

export const buildWhySizeRationale = (currentPick, recPick, language = "en",
                                       hasRecommendedBaseline = true) => {
  const { buyerNotes, autoReasoning } = buildWhySizeParts(
    currentPick, recPick, language, hasRecommendedBaseline);
  if (buyerNotes.length === 0) return autoReasoning;
  let reasoning = buyerNotes.join(" ");
  if (autoReasoning) {
    reasoning += language === "fr"
      ? ` (Recommandation initiale : ${autoReasoning})`
      : ` (Original recommendation: ${autoReasoning})`;
  }
  return reasoning;
};

const _thermalLabel = (tech, language) => {
  if (tech === "gas_engine") return language === "fr" ? "Moteurs à gaz" : "Gas engines";
  return String(tech || "").toUpperCase();
};

/**
 * Small "why this size?" popover, anchored to an info icon.
 *
 * Renders one green highlight box per buyer-authored note (if any) and a
 * grey italic block for the "Original recommendation" auto text. Accepts
 * a single pick (used by the Adjust panel) OR a list of picks (used by
 * the Recommended Mix card so lenders can trace all thermal choices from
 * one place).
 *
 * Props:
 *   testid       — data-testid stem; the icon uses `${testid}`, the popover
 *                  uses `${testid}-popover`, etc.
 *   ariaLabel    — accessible label for the icon button.
 *   language     — "en" | "fr".
 *   picks        — array of `{ label, buyerNotes, autoReasoning }` blocks.
 *                  When one block has buyerNotes, the icon flips to the
 *                  Sparkles variant so lenders spot an edited pick at a
 *                  glance.
 */
export const WhySizePopover = ({ testid, ariaLabel, picks, language = "en" }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const fr = language === "fr";
  const validPicks = (picks || []).filter((p) => p && (p.buyerNotes?.length > 0 || (p.autoReasoning || "").trim()));
  if (validPicks.length === 0) return null;
  const anyBuyerNotes = validPicks.some((p) => (p.buyerNotes || []).length > 0);
  // Joined text mirrors what the PDF mapper prints, one paragraph per
  // pick with a blank line separator when there are multiple picks.
  const joinedText = validPicks.map((p) => {
    const notes = (p.buyerNotes || []).join(" ");
    const auto = (p.autoReasoning || "").trim();
    if (notes && auto) {
      return `[${p.label}] ${notes}${fr ? ` (Recommandation initiale : ${auto})` : ` (Original recommendation: ${auto})`}`;
    }
    return `[${p.label}] ${notes || auto}`;
  }).join("\n\n");
  const copy = async () => {
    try { await navigator.clipboard.writeText(joinedText); setCopied(true); setTimeout(() => setCopied(false), 1400); }
    catch { /* clipboard not available — fine, native title still holds the text */ }
  };
  return (
    <span ref={rootRef} className="relative inline-flex items-center" data-testid={`${testid}-root`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title={joinedText}
        aria-label={ariaLabel}
        aria-expanded={open}
        data-testid={testid}
        style={{
          color: "var(--brand-green)",
          background: "transparent", border: "none", cursor: "pointer",
          padding: 0, display: "inline-flex", alignItems: "center",
        }}
      >
        {anyBuyerNotes ? <Sparkles size={13} /> : <Info size={13} />}
      </button>
      {open && (
        <div
          role="dialog"
          aria-modal="false"
          data-testid={`${testid}-popover`}
          className="absolute z-50 rounded-lg shadow-lg text-xs"
          style={{
            top: "calc(100% + 6px)", left: 0, width: 340, maxWidth: "88vw",
            background: "var(--white)", border: "1px solid var(--rule)",
            padding: 12, color: "var(--char)", lineHeight: 1.45,
          }}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="text-[10px] font-bold uppercase tracking-wider"
                  style={{ color: "var(--brand-green)" }}>
              {fr ? "Pourquoi cette taille ?" : "Why this size?"}
            </div>
            <button type="button" onClick={() => setOpen(false)}
                    aria-label={fr ? "Fermer" : "Close"}
                    data-testid={`${testid}-close`}
                    style={{ background: "transparent", border: "none",
                              color: "var(--muted)", cursor: "pointer", padding: 0 }}>
              <X size={14} />
            </button>
          </div>
          <div className="space-y-3">
            {validPicks.map((p, idx) => {
              const hasBuyer = (p.buyerNotes || []).length > 0;
              return (
                <div key={idx} data-testid={`${testid}-block-${p.label}`}>
                  {validPicks.length > 1 && (
                    <div className="text-[10px] font-bold uppercase tracking-wider mb-1"
                          style={{ color: "var(--char)" }}>
                      {p.label}
                    </div>
                  )}
                  {hasBuyer && (
                    <div
                      className="rounded p-2 mb-1"
                      data-testid={`${testid}-buyer-${p.label}`}
                      style={{
                        background: "#E4F3E7", border: "1px solid #B9DFC1",
                        color: "#1A6F2B", fontWeight: 500,
                      }}
                    >
                      {p.buyerNotes.map((n, i) => (
                        <div key={i} className={i > 0 ? "mt-1" : ""}>{n}</div>
                      ))}
                    </div>
                  )}
                  {p.autoReasoning && (
                    <div data-testid={`${testid}-auto-${p.label}`}>
                      {hasBuyer && (
                        <div className="text-[10px] font-bold uppercase tracking-wider mb-1"
                              style={{ color: "var(--muted)" }}>
                          {fr ? "Recommandation initiale" : "Original recommendation"}
                        </div>
                      )}
                      <div style={{ color: "var(--muted)", fontStyle: hasBuyer ? "italic" : "normal" }}>
                        {p.autoReasoning}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-3 pt-2"
                style={{ borderTop: "1px dashed var(--rule)" }}>
            <div className="text-[10px]" style={{ color: "var(--muted)" }}>
              {fr ? "Aperçu du texte imprimé sur le PDF" : "Preview of PDF report copy"}
            </div>
            <button type="button" onClick={copy}
                    data-testid={`${testid}-copy`}
                    className="text-[10px] font-bold uppercase tracking-wider"
                    style={{ background: "transparent", border: "none",
                              color: "var(--brand-green)", cursor: "pointer", padding: 0 }}>
              {copied ? (fr ? "Copié" : "Copied") : (fr ? "Copier" : "Copy")}
            </button>
          </div>
        </div>
      )}
    </span>
  );
};

/**
 * Convenience helper: build the popover picks array for a whole thermal
 * mix (one block per pick). Used by Results.jsx to attach the same
 * green/grey diff popover to the "Gas recip" tile of the Recommended and
 * Adjusted cards.
 */
export const buildThermalMixPicks = (thermalPicks, recPicks, language = "en") => {
  const hasBaseline = (recPicks || []).length > 0;
  const recByTech = Object.fromEntries((recPicks || []).map((p) => [p.tech, p]));
  return (thermalPicks || [])
    .filter((p) => Number(p?.count || 0) > 0)
    .map((p) => {
      const { buyerNotes, autoReasoning } = buildWhySizeParts(
        p, recByTech[p.tech], language, hasBaseline);
      return {
        label: _thermalLabel(p.tech, language),
        buyerNotes, autoReasoning,
      };
    });
};
